import { createFileRoute, useSearch, useNavigate } from '@tanstack/react-router'
import { useEffect, useState, useRef } from 'react'
import { createSafeStorage } from '@/src/lib/typedStorage'
import { SessionStorageKey, SessionStorageModel, PendingDrawing } from '@/storage.config'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { listTemplates } from '@/src/api/template'
import { getDrawingsByIds, getDrawingByShareId, getRecentDrawings } from '@/src/api/drawing'
import { getSignedUrl } from '@/src/api/file'
import { getFileKey, getPdfKey } from '@/shared/lib/s3Keys'
import { toast } from 'sonner'
import { LoadingView } from '@/src/lib/results/LoadingView'
import { ResultsView } from '@/src/lib/results/ResultsView'
import { useTranslation } from '@/src/i18n/client'
import { CaretLeftIcon, CaretRightIcon } from '@phosphor-icons/react'
import { normalizeMimeType } from '@/src/lib/fileFormat'

export const Route = createFileRoute('/results/')({
  component: ResultsPage,
  validateSearch: (search: Record<string, unknown>): { shareId?: string } => ({
    shareId: typeof search.shareId === 'string' ? search.shareId : undefined,
  }),
})

const sessionStorageService = createSafeStorage<SessionStorageModel>(() => window.sessionStorage)

function ResultsPage() {
  const { t } = useTranslation('results')
  const { shareId } = useSearch({ from: '/results/' })
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [analysisResult, setAnalysisResult] = useState<SessionStorageModel[SessionStorageKey.DrawingData] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [fileContent, setFileContent] = useState<SessionStorageModel[SessionStorageKey.DrawingFiles] | null>(null)
  const [pendingDrawings, setPendingDrawings] = useState<PendingDrawing[] | null>(null)
  const [completedCount, setCompletedCount] = useState(0)
  const [totalCount, setTotalCount] = useState(0)
  const [templateOptions, setTemplateOptions] = useState([{
    name: 'defaultDrawingTemplate',
    label: 'Full JSON Output',
    type: 'application/json',
    content: ''
  }])
  const [loadingFromShareId, setLoadingFromShareId] = useState(!!shareId)
  
  // Track whether we've shown the completion toast
  const hasShownCompletionToast = useRef(false)

  // Fetch recent drawings for job-to-job navigation
  const { data: recentDrawings } = useQuery({
    queryKey: ['recentDrawings'],
    queryFn: () => getRecentDrawings({ data: { limit: 50 } }),
    enabled: !!shareId, // Only fetch when viewing a specific drawing
  })

  // Find current position in history
  const currentIndex = recentDrawings?.findIndex((d: any) => d.shareId === shareId) ?? -1
  const hasPrevious = currentIndex > 0
  const hasNext = currentIndex >= 0 && currentIndex < (recentDrawings?.length ?? 0) - 1
  const previousShareId = hasPrevious ? recentDrawings?.[currentIndex - 1]?.shareId : null
  const nextShareId = hasNext ? recentDrawings?.[currentIndex + 1]?.shareId : null

  // Keyboard navigation between jobs
  useEffect(() => {
    if (!shareId) return // Only enable when viewing specific drawing

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft' && hasPrevious && previousShareId) {
        navigate({ to: '/results', search: { shareId: previousShareId } })
      } else if (e.key === 'ArrowRight' && hasNext && nextShareId) {
        navigate({ to: '/results', search: { shareId: nextShareId } })
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [shareId, hasPrevious, hasNext, previousShareId, nextShareId, navigate])

  // Fetch drawing by shareId with automatic polling
  const { data: drawingByShareId, isLoading: isLoadingByShareId } = useQuery({
    queryKey: ['drawing', 'byShareId', shareId],
    queryFn: () => getDrawingByShareId({ data: { shareId: shareId! } }),
    enabled: !!shareId,
    refetchInterval: (query) => {
      // Keep polling if drawing is still processing
      const data = query.state.data
      if (data?.status === 'PROCESSING') {
        return 3000 // Poll every 3 seconds while processing
      }
      return false // Stop polling once complete/failed
    },
    refetchIntervalInBackground: false, // Don't poll when tab is inactive
  })

  // Handle loading drawing by shareId
  useEffect(() => {
    if (!shareId || isLoadingByShareId) return
    
    if (drawingByShareId) {
      // Drawing found and completed - extract content
      if (drawingByShareId.status === 'COMPLETED') {
        // Show toast notification if this is a newly completed drawing
        if (pendingDrawings && pendingDrawings.length > 0 && !hasShownCompletionToast.current) {
          toast.success(t('resultsPage.analysisComplete'), {
            duration: 6000,
          })
          hasShownCompletionToast.current = true
        }
        
        // Use effective data from API (merged master + overrides)
        const content = (drawingByShareId as any).effectiveData || {}
        setAnalysisResult(content)
        
        // Clear pending state
        setPendingDrawings(null)
        
        // Load file content for PDF viewer
        const loadFileContent = async () => {
          if (drawingByShareId.files && drawingByShareId.files.length > 0) {
            const file = drawingByShareId.files[0]
            if (file?.id && file?.name) {
              try {
                const pdfKey = getPdfKey(file.id, file.name)
                const fileKey = getFileKey(file.id, file.name)
                const signedPdf = await getSignedUrl({ data: { key: pdfKey } })
                const signedResult = signedPdf?.url
                  ? signedPdf
                  : await getSignedUrl({ data: { key: fileKey } })
                if (signedResult?.url) {
                  // Set file content in the format expected by FileViewer
                  setFileContent([{
                    name: file.name,
                    type: 'application/pdf',
                    data: signedResult.url, // Use signed URL directly
                  }])
                }
              } catch (err) {
                console.error(t('resultsPage.failedToLoadFile'), err)
              }
            }
          }
        }
        loadFileContent()
        
        setLoadingFromShareId(false)
      } else if (drawingByShareId.status === 'PROCESSING') {
        // Start polling for this drawing
        if (!pendingDrawings) {
          setPendingDrawings([{
            drawingId: drawingByShareId.id,
            fileId: '',
            fileName: drawingByShareId.name || '',
            startedAt: drawingByShareId.createdAt,
          }])
          setTotalCount(1)
        }
        setLoadingFromShareId(false)
      } else if (drawingByShareId.status === 'FAILED') {
        toast.error(t('resultsPage.analysisFailedMessage', { message: drawingByShareId.errorMessage || 'Unknown error' }), {
          duration: 8000,
        })
        setError(t('resultsPage.drawingAnalysisFailed', { message: drawingByShareId.errorMessage || 'Unknown error' }))
        setLoadingFromShareId(false)
        setPendingDrawings(null)
      }
    } else {
      setError(t('resultsPage.drawingNotFound'))
      setLoadingFromShareId(false)
    }
  }, [shareId, drawingByShareId, isLoadingByShareId, pendingDrawings, t])

  // Fetch templates
  const { data: templates } = useQuery({
    queryKey: ['templates'],
    queryFn: () => listTemplates({ data: {} }),
  })

  // Polling for drawings from session storage (upload flow)
  const drawingIdsToFetch = pendingDrawings?.map(p => p.drawingId) || []
  
  const { data: polledDrawings } = useQuery({
    queryKey: ['drawings', 'byIds', drawingIdsToFetch],
    queryFn: () => getDrawingsByIds({ data: { ids: drawingIdsToFetch } }),
    enabled: !shareId && drawingIdsToFetch.length > 0 && analysisResult === null,
    refetchInterval: (query) => {
      // Check if any drawings are still processing
      const data = query.state.data
      const hasProcessing = data?.some((d: any) => d.status === 'PROCESSING')
      return hasProcessing ? 3000 : false // Poll every 3 seconds while processing
    },
    refetchIntervalInBackground: false,
  })

  // Handle polled drawings from session storage
  useEffect(() => {
    if (!polledDrawings || polledDrawings.length === 0 || shareId) return
    
    const completed = polledDrawings.filter((d: any) => d.status === 'COMPLETED')
    const failed = polledDrawings.filter((d: any) => d.status === 'FAILED')
    const processing = polledDrawings.filter((d: any) => d.status === 'PROCESSING')
    
    setCompletedCount(completed.length + failed.length)
    
    // If all drawings are done (completed or failed)
    if (processing.length === 0) {
      // Show toast notification
      if (!hasShownCompletionToast.current) {
        if (completed.length > 0 && failed.length === 0) {
          toast.success(t('resultsPage.analysisComplete'), {
            duration: 6000,
          })
        } else if (failed.length > 0 && completed.length > 0) {
          toast.warning(t('resultsPage.analysisCompleteWithFailures', { count: failed.length }), {
            duration: 8000,
          })
        } else if (failed.length > 0) {
          toast.error(t('resultsPage.analysisFailed'), {
            duration: 8000,
          })
        }
        hasShownCompletionToast.current = true
      }
      
      // Check for errors
      if (failed.length > 0) {
        const errorMessages = failed
          .map((d: any) => d.errorMessage || `Drawing ${d.id} failed`)
          .join(', ')
        setError(t('resultsPage.someDrawingsFailed', { errors: errorMessages }))
      }
      
      // Use the first completed drawing's content
      if (completed.length > 0) {
        const firstCompleted = completed[0]
        // Use effective data from API (merged master + overrides)
        const content = (firstCompleted as any).effectiveData || {}
        setAnalysisResult(content)
        
        // Update session storage
        sessionStorageService.set(SessionStorageKey.DrawingData, content)
        sessionStorageService.remove(SessionStorageKey.PendingDrawings)
      }
      
      // Clear pending drawings
      setPendingDrawings(null)
    }
  }, [polledDrawings, shareId, t])

  // Update template options when templates load
  useEffect(() => {
    if (templates && Array.isArray(templates)) {
      const defaultOption = {
        name: 'defaultDrawingTemplate',
        label: 'Full JSON Output',
        type: 'application/json',
        content: ''
      }
      // Filter templates to only show RESULTS templates (exclude WORKFLOW, EMAIL, etc.)
      const resultsTemplates = templates.filter((t: any) => t.usage === 'RESULTS' || !t.usage)
      const templateOpts = resultsTemplates.map(t => ({
        name: t.name,
        label: t.description ?? t.name,
        type: normalizeMimeType(t.type),
        content: t.content,
        undefinedValue: t.undefinedValue ?? ''
      }))
      setTemplateOptions([defaultOption, ...templateOpts])
    }
  }, [templates])

  // Load data from sessionStorage on mount (skip if loading by shareId)
  useEffect(() => {
    // Skip session storage loading if we have a shareId
    if (shareId) return
    
    const drawingData = sessionStorageService.get(SessionStorageKey.DrawingData)
    const files = sessionStorageService.get(SessionStorageKey.DrawingFiles)
    const pending = sessionStorageService.get(SessionStorageKey.PendingDrawings)
    
    setFileContent(files ?? null)
    
    // If we have pending drawings, set them up for polling
    if (pending && pending.length > 0) {
      setPendingDrawings(pending)
      setTotalCount(pending.length)
    } else if (drawingData) {
      // We already have completed drawing data
      setAnalysisResult(drawingData)
    }
  }, [shareId])

  // Warn user before leaving page while analysis is pending
  useEffect(() => {
    const isProcessing = pendingDrawings && pendingDrawings.length > 0 && !analysisResult
    if (!isProcessing) return

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = t('resultsPage.analysisInProgress')
      return e.returnValue
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [pendingDrawings, analysisResult, t])

  if (error) {
    return (
      <div className="w-full h-full flex items-center justify-center p-4">
        <div role="alert" className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
          <strong className="font-bold">{t('resultsPage.error')}</strong> {error}
        </div>
      </div>
    )
  }

  // Show loading state while loading from shareId
  if (loadingFromShareId || isLoadingByShareId) {
    return <LoadingView />
  }

  // Show results view (handles both polling and completed states)
  return (
    <div className="w-full relative group" style={{ height: 'calc(100dvh - 96px)' }}>
      {/* Job navigation arrows (when viewing from history) - show on hover */}
      {shareId && recentDrawings && recentDrawings.length > 1 && (
        <>
          {/* Left hover zone */}
          {hasPrevious && (
            <div className="absolute left-0 top-0 bottom-0 w-24 z-40 group/left">
              <button
                onClick={() => navigate({ to: '/results', search: { shareId: previousShareId! } })}
                className="absolute left-2 top-1/2 -translate-y-1/2 z-50 w-12 h-12 flex items-center justify-center bg-background/60 border border-border/50 rounded-full shadow-lg hover:bg-background/80 hover:scale-110 transition-all opacity-0 group-hover/left:opacity-100"
                title="Previous job (←)"
              >
                <CaretLeftIcon size={24} />
              </button>
            </div>
          )}
          
          {/* Right hover zone */}
          {hasNext && (
            <div className="absolute right-0 top-0 bottom-0 w-24 z-40 group/right">
              <button
                onClick={() => navigate({ to: '/results', search: { shareId: nextShareId! } })}
                className="absolute right-2 top-1/2 -translate-y-1/2 z-50 w-12 h-12 flex items-center justify-center bg-background/60 border border-border/50 rounded-full shadow-lg hover:bg-background/80 hover:scale-110 transition-all opacity-0 group-hover/right:opacity-100"
                title="Next job (→)"
              >
                <CaretRightIcon size={24} />
              </button>
            </div>
          )}
        </>
      )}
      
      <ResultsView
        analysisResult={analysisResult}
        fileContent={fileContent}
        templateOptions={templateOptions}
        onError={setError}
        pendingDrawings={pendingDrawings}
        completedCount={completedCount}
        totalCount={totalCount}
      />
    </div>
  )
}
